<template>
  <HLModal
    :show="isOpen"
    :show-header="true"
    :show-footer="true"
    :show-close="true"
    :mask-closable="true"
    :close-on-esc="true"
    :show-header-icon="false"
    width="600"
    class="project-selector-modal"
    @update:show="(value) => !value && $emit('cancel')"
  >
    <template #header>
      <div class="flex items-center gap-3 px-2 pt-5 pb-4">
        <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30">
          <HLIcon
            :size="20"
            class="text-primary-600 dark:text-primary-400"
          >
            <MaterialBugReportIcon />
          </HLIcon>
        </div>
        <div>
          <HLText
            size="2xl"
            weight="semibold"
          >
            {{ title }}
          </HLText>
          <HLText
            size="sm"
            color="muted"
          >
            {{ description }}
          </HLText>
        </div>
      </div>
    </template>

    <template #default>
      <div class="space-y-5 px-2">
        <!-- Project Select Dropdown -->
        <HLFormItem>
          <template #label>
            Select a Project <span class="text-red-500">*</span>
          </template>
          <HLSelect
            id="project-selector"
            v-model:value="selectedProjectId"
            :options="projectOptions"
            :filter="filterProjectOption"
            placeholder="Search or select a project..."
            filterable
            size="sm"
            :aria-label="'Select a project'"
            class="w-full"
          />
        </HLFormItem>

        <!-- Helpful Note (only after a project is selected) -->
        <div
          v-if="selectedProjectId"
          class="create-bug-note"
        >
          <div class="flex items-start space-x-3">
            <HLIcon
              :size="20"
              class="flex-shrink-0 mt-0.5"
            >
              <InfoCircleIcon />
            </HLIcon>
            <HLText
              size="md"
            >
              Before creating a new bug, please check the existing bugs list to ensure there are no duplicates.
              <HLButton
                variant="text"
                color="blue"
                size="xs"
                @click="goToBugsList"
              >
                View Bugs List
              </HLButton>
            </HLText>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3 px-5 py-4">
        <HLButton
          id="project-selector-cancel"
          type="button"
          color="gray"
          variant="secondary"
          size="xs"
          @click="$emit('cancel')"
        >
          Cancel
        </HLButton>
        <HLButton
          id="project-selector-confirm"
          color="primary"
          variant="primary"
          size="xs"
          :disabled="!selectedProjectId"
          @click="confirmSelection"
        >
          Continue
        </HLButton>
      </div>
    </template>
  </HLModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { HLModal, HLButton, HLSelect, HLText, HLIcon, HLFormItem } from '@platform-ui/highrise'
import { InfoCircleIcon } from '@gohighlevel/ghl-icons/24/outline'
import { MaterialBugReportIcon } from '@gohighlevel/ghl-icons/24/material/rounded'

const router = useRouter()

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Report a Bug'
  },
  description: {
    type: String,
    default: 'Choose a project to report a bug in'
  },
  projects: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['confirm', 'cancel'])

const selectedProjectId = ref(null)

const projectOptions = computed(() => {
  return props.projects.map(project => ({
    label: project.name,
    value: project.id,
    description: project.description,
  }))
})

const filterProjectOption = (pattern, option) => {
  if (!pattern) return true
  return String(option?.label ?? '')
    .toLowerCase()
    .includes(String(pattern).toLowerCase())
}

const selectedProject = computed(() => {
  return props.projects.find(p => p.id === selectedProjectId.value)
})

const confirmSelection = () => {
  if (selectedProject.value) {
    emit('confirm', selectedProject.value)
    selectedProjectId.value = null
  }
}

const goToBugsList = () => {
  if (!selectedProjectId.value) return
  router.push(`/projects/${selectedProjectId.value}/bugs`)
  emit('cancel')
}

// Reset on close
watch(() => props.isOpen, (newVal) => {
  if (!newVal) {
    selectedProjectId.value = null
  }
})
</script>

<style scoped>
/* Hide the default info icon from HLModal header */
:deep(.hl-modal__header .hl-icon) {
  display: none !important;
}

.create-bug-note {
  background-color: var(--gray-100);
  padding: 16px;
  border-radius: 8px;
}
</style>
